'use client'

import { FormRadio, FormRadioGroup, useFormContext } from '@ariakit/react'
import capitalize from 'lodash-es/capitalize'
import isEmpty from 'lodash-es/isEmpty'
import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { Select, SelectOption, TypeStyleHeadlineMicro } from '@syconium/little-miss-figgy'

import { useStaticAmplitudeTracking } from '../../_hooks/useStaticTracking'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { FormError } from '../form/FormError'
import { useIsValidFormField } from '../form/useIsValidFormField'

import {
  VariantSelectionFieldProps,
  VariantSelectionFieldValueDisplay,
  VariantSelectionFormContext,
  VariantSelectionFormGroupLabel,
  VariantSelectionFormGroupLabelPrimaryText,
  VariantSelectionFormState,
  VariantSelectionFromSoldOutMessage,
  VariantSelectionInvisibleInput,
  VariantSelectionOption,
  VariantSelectionOptionsRow,
} from './VariantSelectionForm'

export const useVariantSelectionFitField = (props?: Partial<VariantSelectionFieldProps>) => {
  const { product, variantGroup } = useContext(VariantSelectionFormContext)!
  const translations = useTranslation()
  const formContext = useFormContext()

  // Keep a local version of value, not just computed from context. So we can quickly rerender
  // with a checked state without waiting for a context rerender which can include a lot of other
  // components.
  const outerValue = formContext!.useValue<VariantSelectionFormState['fit']>('fit') ?? ''
  const [value, _setValue] = useState(outerValue)
  const pendingOuterUpdate = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const setValue = useCallback(
    (newValue: typeof outerValue) => {
      // Update inner value with high priority render.
      _setValue(newValue)

      // Deprioritize any external state changed via onChange prop. Only the latest selection is
      // forwarded, so rapid successive changes cannot leave stale writes queued behind it.
      clearTimeout(pendingOuterUpdate.current)
      pendingOuterUpdate.current = setTimeout(() => {
        formContext!.setValue('fit', newValue)
      })
    },
    [formContext]
  )

  useUpdateEffect(() => {
    // Sync internal value to match the new one provided. This must not write back to the form,
    // otherwise two in-flight selections echo each other and loop endlessly.
    _setValue(outerValue)

    // This is on purpose. We trigger an on change like this instead of via the input so that if there are two
    // of these fields in one form, the on change for both fires if either is altered.
    // This allows variant selection fields in nested embroidery modals.
    props?.onChange?.(outerValue)
  }, [outerValue])

  const [initialValue, _] = useState(value)
  const option = product.options.fits?.find(fitOption => fitOption.fit.id === value)
  const displayValue = option?.fit.displayValue ?? value
  const hide = !isEmpty(initialValue) && product.options.fits?.length === 1
  const hasASizeInStock = !!product.options.sizes?.find(size => size.outOfStock === false)
  const renderSoldOutLabel =
    option?.outOfStock && variantGroup.availability === 'AVAILABLE' && !hasASizeInStock

  formContext!.useValidate(() => {
    const fitFieldName = 'fit' as const
    const value = formContext!.getValue(fitFieldName)
    if (!value || value.length === 0) {
      formContext!.setError(
        fitFieldName,
        translations.selectAndAddVariant.fitSectionRequiredMessage
      )
    }
  })

  const soldOutLabelPostfix = useMemo(() => {
    return renderSoldOutLabel ? (
      <>
        &nbsp;
        <VariantSelectionFromSoldOutMessage />
      </>
    ) : null
  }, [renderSoldOutLabel])

  const isValid = useIsValidFormField({ name: 'fit' })

  const renderAsDisplayValue = useCallback(
    (valueToRender: string) => {
      const valueIsSelected = valueToRender === value
      const valueAsFitOption = product.options.fits?.find(
        fitOption => fitOption.fit.id === valueToRender
      )
      const valueIsOutOfStock = valueAsFitOption?.outOfStock === true
      const renderAsOutOfStock = valueIsSelected
        ? valueIsOutOfStock && !hasASizeInStock
        : valueIsOutOfStock
      const valueAsDisplayText = valueAsFitOption?.fit.displayValue ?? valueToRender
      const fitDisplayText = isEmpty(valueToRender)
        ? translations.cart.selectFit
        : renderAsOutOfStock
        ? `${capitalize(valueAsDisplayText)} ${translations.giftCard.dropdownSoldOutOptionText}`
        : capitalize(valueAsDisplayText)
      return <TypeStyleHeadlineMicro>{fitDisplayText}</TypeStyleHeadlineMicro>
    },
    [
      hasASizeInStock,
      product.options.fits,
      translations.cart.selectFit,
      translations.giftCard.dropdownSoldOutOptionText,
      value,
    ]
  )

  const labelText =
    variantGroup.primaryCategory.key === 'SCRUB_TOP'
      ? translations.pages.product.scrubTopsFitTitle
      : variantGroup.primaryCategory.key === 'SCRUB_PANTS'
      ? translations.pages.product.scrubPantsFitTitle
      : translations.magnolia.reviewsPage.filters.filter.Fit.title

  return {
    labelText,
    labelDisplayValue: capitalize(displayValue),
    displayValue,
    soldOutLabelPostfix,
    value,
    renderAsDisplayValue,
    setValue,
    isValid,
    hide,
    hasASizeInStock,
  }
}

type VariantSelectionFitRadioGroupProps = {
  className?: string
} & VariantSelectionFieldProps

export const VariantSelectionFitRadioGroup = ({
  className,
  ...baseProps
}: VariantSelectionFitRadioGroupProps) => {
  const { product, secondaryTrackingLocation } = useContext(VariantSelectionFormContext)!
  const {
    hasASizeInStock,
    value,
    hide,
    labelText,
    labelDisplayValue,
    setValue,
    soldOutLabelPostfix,
  } = useVariantSelectionFitField(baseProps)

  const { trackStaticAmplitudeEvent } = useStaticAmplitudeTracking()

  return (
    <div style={{ display: hide ? 'none' : 'block' }} className={className}>
      <FormRadioGroup data-testid='fit-picker'>
        <StyledVariantSelectionFormGroupLabel>
          <VariantSelectionFormGroupLabelPrimaryText>
            {labelText}
            {labelDisplayValue ? `: ` : ` `}
          </VariantSelectionFormGroupLabelPrimaryText>
          {labelDisplayValue.length > 0 ? (
            <VariantSelectionFieldValueDisplay>
              {labelDisplayValue}
              {soldOutLabelPostfix}
            </VariantSelectionFieldValueDisplay>
          ) : null}
        </StyledVariantSelectionFormGroupLabel>
        <VariantSelectionOptionsFitRow>
          {product.options.fits?.map(fitOption => {
            const fit = fitOption.fit
            const isSelected = value === fit.id
            const disabled = isSelected
              ? fitOption.outOfStock && !hasASizeInStock
              : fitOption.outOfStock
            return (
              <FormRadio
                name={'fit'}
                key={fit.id}
                value={fit.id}
                required
                checked={isSelected}
                onClick={() =>
                  trackStaticAmplitudeEvent({
                    productHandle: product.handle,
                    secondaryTrackingLocation,
                    modifiedElement: 'Fit Selector',
                    modifiedValue: fit.id,
                  })
                }
                render={htmlInputProps => {
                  return (
                    <Label
                      htmlFor={htmlInputProps.id}
                      data-fit-availability={disabled ? 'unavailable' : 'available'}
                    >
                      <VariantSelectionInvisibleInput
                        {...htmlInputProps}
                        onChange={event => {
                          if (event.target.checked) setValue(fit.id)
                        }}
                        aria-label={fit.displayValue}
                      />
                      <VariantSelectionOption
                        aria-hidden={true}
                        aria-label={fit.displayValue}
                        isSelected={isSelected}
                        soldOut={disabled}
                        as='div'
                        refreshedColors
                        accentColor={undefined}
                      >
                        {capitalize(fit.displayValue)}
                      </VariantSelectionOption>
                    </Label>
                  )
                }}
              />
            )
          })}
        </VariantSelectionOptionsFitRow>
        <FormError name='fit' />
      </FormRadioGroup>
    </div>
  )
}

export const VariantSelectionFitSelect = (baseProps: VariantSelectionFieldProps) => {
  const { product, secondaryTrackingLocation } = useContext(VariantSelectionFormContext)!
  const { trackStaticAmplitudeEvent } = useStaticAmplitudeTracking()
  const { hide, labelText, setValue, renderAsDisplayValue, value, isValid } =
    useVariantSelectionFitField(baseProps)

  const onChange = useCallback(
    (newValue: string) => {
      setValue(newValue)
      trackStaticAmplitudeEvent({
        productHandle: product.handle,
        secondaryTrackingLocation,
        modifiedElement: 'Fit Selector',
        modifiedValue: newValue,
      })
    },
    [product.handle, secondaryTrackingLocation, setValue, trackStaticAmplitudeEvent]
  )

  return (
    <div style={{ display: hide ? 'none' : 'block' }}>
      <Select
        value={value}
        required
        onChange={onChange}
        name={'fit'}
        renderSelectValue={renderAsDisplayValue}
        isInvalid={!isValid}
        heading={labelText}
      >
        {product.options.fits?.map(fitOption => {
          const fit = fitOption.fit
          return (
            <SelectOption key={fit.id} value={fit.id}>
              {renderAsDisplayValue(fit.id)}
            </SelectOption>
          )
        })}
      </Select>
      <FormError name='fit' />
    </div>
  )
}

const Label = styled.label`
  flex: 1;
`

const VariantSelectionOptionsFitRow = styled(VariantSelectionOptionsRow)`
  margin-top: ${o => o.theme.rowGap.small};
`

const StyledVariantSelectionFormGroupLabel = styled(VariantSelectionFormGroupLabel)`
  margin-bottom: 0;
`
